import { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";

import { Link } from "react-router-dom";

import { FIND_PERSON_COMPANY_CREDITS } from "@signalist/shared-types";

import {
	FIND_PEOPLE_LOOKUP_TIMINGS,
	FIND_PEOPLE_QUOTE_STEPS,
	FindPeopleLookup,
	FindPeopleQuote,
} from "@/pages/Private/redux/list/list-find-person.api";

const PREFIX = "defineIcps.bulkFindPeople";

type LookupKind = FindPeopleLookup["kind"];
type LookupTiming = (typeof FIND_PEOPLE_LOOKUP_TIMINGS)[number];
type QuoteStep = (typeof FIND_PEOPLE_QUOTE_STEPS)[number];

/** Credits as the dialog writes them: whole numbers with separators, at most two decimals. */
export const formatCredits = (credits: number): string =>
	credits.toLocaleString("en-US", { maximumFractionDigits: 2 });

/** The label of each lookup, under the dialog's prefix: one key per kind. */
export const LOOKUP_LABEL_KEY: Record<LookupKind, string> = {
	EMAIL: "lookupLabel.email",
	PHONE: "lookupLabel.phone",
	COMPANY_CONTACT: "lookupLabel.companyContact",
};

/** The translation key for a lookup's sentence: one per kind and timing, never built from either. */
export const LOOKUP_TEXT_KEY: Record<LookupKind, Record<LookupTiming, string>> = {
	EMAIL: {
		ON_ADD: "lookupText.emailOnAdd",
		ON_ADD_WHEN_NO_EMAIL: "lookupText.emailOnAdd",
		ON_ASSIGN: "lookupText.onAssign",
		ON_SEND: "lookupText.onSend",
		OFF: "lookupText.off",
	},
	PHONE: {
		ON_ADD: "lookupText.phoneOnAdd",
		ON_ADD_WHEN_NO_EMAIL: "lookupText.phoneOnAdd",
		ON_ASSIGN: "lookupText.onAssign",
		ON_SEND: "lookupText.onSend",
		OFF: "lookupText.off",
	},
	COMPANY_CONTACT: {
		ON_ADD: "lookupText.companyContactOnAdd",
		ON_ADD_WHEN_NO_EMAIL: "lookupText.companyContactWhenNoEmail",
		ON_ASSIGN: "lookupText.onAssign",
		ON_SEND: "lookupText.onSend",
		OFF: "lookupText.off",
	},
};

/**
 * How each charged step's line reads, from the server's units: the text key and the unit
 * phrase it fills. The find person line names the companies and people per company instead.
 */
export const QUOTE_LINE: Record<
	Exclude<QuoteStep, "FIND_PERSON">,
	{ key: string; unitKey: string; unitParam: string }
> = {
	EMAIL: { key: "costLookup.email", unitKey: "upToPeople", unitParam: "people" },
	PHONE: { key: "costLookup.phone", unitKey: "upToPeople", unitParam: "people" },
	COMPANY_CONTACT: {
		key: "costLookup.companyContact",
		unitKey: "upToLookups",
		unitParam: "lookups",
	},
	AI_COLUMNS: { key: "costLookup.aiColumns", unitKey: "upToColumnRuns", unitParam: "runs" },
};

interface FindPeopleLookupsSectionProps {
	listId: number;
	lookups: FindPeopleLookup[];
	isTracker: boolean;
}

/**
 * "For the people found": when the lookups the list's settings ask for run for them and what one
 * costs, as the API decided it, and where the people land. A company contact lookup the list does
 * not ask for is left out, as the mockup shows email and phone only.
 */
export const FindPeopleLookupsSection: FunctionComponent<FindPeopleLookupsSectionProps> = ({
	listId,
	lookups,
	isTracker,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`${PREFIX}.${key}`, options ?? {});
	const shown = lookups.filter(
		(lookup) => lookup.kind !== "COMPANY_CONTACT" || lookup.timing !== "OFF"
	);

	return (
		<section className="flex flex-col gap-2 rounded-[10px] border border-border-light px-4 py-3.5">
			<div className="flex items-center justify-between">
				<span className="text-sm font-bold">{ts("forPeopleFound")}</span>
				<Link
					className="text-xs font-bold text-brand-light"
					to={`/app/lists/create/settings/${listId}`}
				>
					{ts("changeInListSettings")}
				</Link>
			</div>
			{shown.map((lookup) => (
				<div key={lookup.kind} className="text-ssm" data-testid={`lookup-${testIdOf(lookup.kind)}`}>
					<span className="font-bold">{ts(LOOKUP_LABEL_KEY[lookup.kind])}</span>{" "}
					{ts(LOOKUP_TEXT_KEY[lookup.kind][lookup.timing], { count: lookup.unitCredits })}
				</div>
			))}
			<div className="text-ssm">{ts(isTracker ? "placementTracker" : "placementLeads")}</div>
		</section>
	);
};

const testIdOf = (kind: LookupKind): string =>
	({ EMAIL: "email", PHONE: "phone", COMPANY_CONTACT: "companyContact" })[kind];

interface FindPeopleCostBoxProps {
	companies: number;
	quote: FindPeopleQuote;
	balance: number;
	/** What the search's pre-flight asks the balance to cover, when it does not. */
	shortfall: { preflightCredits: number } | null;
}

/** The cost range before the user confirms: the server's quote, line by line, and the total. */
export const FindPeopleCostBox: FunctionComponent<FindPeopleCostBoxProps> = ({
	companies,
	quote,
	balance,
	shortfall,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`${PREFIX}.${key}`, options ?? {});
	const range = ({ min, max }: { min: number; max: number }): string =>
		min === max
			? ts("credits", { count: min })
			: ts("creditRange", { min: formatCredits(min), max: formatCredits(max) });

	return (
		<section
			className="flex flex-col gap-1.5 rounded-[10px] border border-border-light bg-brand-white px-4 py-3.5 text-ssm"
			data-testid="find-people-cost"
		>
			{quote.steps.map(({ kind, maxUnits, credits }) => (
				<div key={kind} className="flex justify-between gap-4">
					<span>
						{kind === "FIND_PERSON"
							? ts("costFindPerson", {
									companies: ts("companies", { count: companies }),
									people: ts("upToPeopleEach", { count: quote.peoplePerCompany }),
								})
							: ts(QUOTE_LINE[kind].key, {
									[QUOTE_LINE[kind].unitParam]: ts(QUOTE_LINE[kind].unitKey, { count: maxUnits }),
								})}
					</span>
					<strong>{range(credits)}</strong>
				</div>
			))}
			<div className="flex justify-between gap-4 border-t border-border-light pt-1.5 text-sm">
				<strong>{ts("total")}</strong>
				<strong data-testid="find-people-total">{range(quote.total)}</strong>
			</div>
			<div className="text-xs text-text-meta">
				{ts("priceNote", {
					credits: FIND_PERSON_COMPANY_CREDITS,
					balance: formatCredits(balance),
				})}
			</div>
			{shortfall && (
				<div className="text-xs font-semibold text-status-error" role="alert">
					{ts("balanceTooLow", {
						balance: formatCredits(balance),
						min: formatCredits(shortfall.preflightCredits),
					})}
				</div>
			)}
		</section>
	);
};
